import { useState, useCallback } from 'react';

interface ConfirmDialogOptions {
    title: string;
    contentText: string;
    dismissButtonLabel?: string;
    actionButtonLabel?: string;
}

interface ConfirmDialogState {
    open: boolean;
    options: ConfirmDialogOptions;
    resolve: ((value: boolean) => void) | null;
}

export const useConfirmDialog = () => {
    const [dialogState, setDialogState] = useState<ConfirmDialogState>({
        open: false,
        options: {
            title: '',
            contentText: '',
            dismissButtonLabel: 'Cancel',
            actionButtonLabel: 'Confirm'
        },
        resolve: null
    });

    const confirm = useCallback((options: ConfirmDialogOptions): Promise<boolean> => {
        return new Promise((resolve) => {
            setDialogState({
                open: true,
                options: {
                    dismissButtonLabel: 'Cancel',
                    actionButtonLabel: 'Confirm',
                    ...options
                },
                resolve
            });
        });
    }, []);

    const handleClose = useCallback(() => {
        if (dialogState.resolve) {
            dialogState.resolve(false);
        }
        setDialogState(prev => ({
            ...prev,
            open: false,
            resolve: null
        }));
    }, [dialogState.resolve]);

    const handleConfirm = useCallback(() => {
        if (dialogState.resolve) {
            dialogState.resolve(true);
        }
        setDialogState(prev => ({
            ...prev,
            open: false,
            resolve: null
        }));
    }, [dialogState.resolve]);

    const handleDismiss = useCallback(() => {
        if (dialogState.resolve) {
            dialogState.resolve(false);
        }
        setDialogState(prev => ({
            ...prev,
            open: false,
            resolve: null
        }));
    }, [dialogState.resolve]);

    return {
        confirm,
        dialogProps: {
            open: dialogState.open,
            onClose: handleClose,
            title: dialogState.options.title,
            contentText: dialogState.options.contentText,
            onClickDismissButton: handleDismiss,
            onClickActionButtonLabel: handleConfirm,
            dismissButtonLabel: dialogState.options.dismissButtonLabel || 'Cancel',
            actionButtonLabel: dialogState.options.actionButtonLabel || 'Confirm'
        }
    };
};